import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';
import { Select } from '../components/ui/Select';
import { SkeletonList } from '../components/ui/Skeleton';
import { kontrollgaengeApi, type Kontrollgang } from '../lib/kontrollgaenge-api';
import { requireAccess } from '../lib/guards';
import { formatGermanDateFromTimestamp } from '../lib/date-format';

export const Route = createFileRoute('/kontrollgaenge/')({
  beforeLoad: () => requireAccess('/api/v1/kontrollgaenge'),
  component: KontrollgaengeIndexPage,
});

type Filter = '' | 'offen' | 'abgeschlossen';

export function KontrollgaengeIndexPage(): JSX.Element {
  const [filter, setFilter] = useState<Filter>('');

  const listQ = useQuery({
    queryKey: ['kontrollgaenge', 'list'],
    queryFn: () => kontrollgaengeApi.list(),
  });

  const items = listQ.data?.items ?? [];
  const filtered = items.filter((k) => {
    if (filter === 'offen') return !k.abgeschlossen;
    if (filter === 'abgeschlossen') return k.abgeschlossen;
    return true;
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Kontrollgänge"
        description="Objekt-bezogene Kontrollen mit Checklisten."
        actions={
          <Link
            to="/kontrollgaenge/neu"
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
          >
            Neuer Kontrollgang
          </Link>
        }
      />

      {listQ.error ? <ErrorBanner error={listQ.error as Error} /> : null}

      <div className="flex flex-wrap gap-3">
        <label className="min-w-[160px] flex-1">
          <span className="mb-1 block text-sm font-medium text-slate-700">Status</span>
          <Select value={filter} onChange={(e) => setFilter(e.target.value as Filter)}>
            <option value="">Alle</option>
            <option value="offen">Offen</option>
            <option value="abgeschlossen">Abgeschlossen</option>
          </Select>
        </label>
      </div>

      {listQ.isLoading ? (
        <SkeletonList />
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
          Keine Kontrollgänge in dieser Auswahl.
        </div>
      ) : (
        <ul className="space-y-3">
          {filtered.map((k) => (
            <KontrollgangCard key={k.id} kontrollgang={k} />
          ))}
        </ul>
      )}
    </div>
  );
}

function KontrollgangCard({ kontrollgang }: { kontrollgang: Kontrollgang }): JSX.Element {
  const ergebnisCount = Object.keys(kontrollgang.ergebnisseJsonb).length;
  return (
    <li>
      <Link
        to="/kontrollgaenge/$id"
        params={{ id: kontrollgang.id }}
        className="block rounded-lg border border-slate-200 bg-white p-4 hover:border-slate-300 hover:shadow-sm"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-base font-medium text-slate-900">
              Kontrollgang {formatGermanDateFromTimestamp(kontrollgang.kontrolliertAm)}
            </div>
            <div className="mt-1 text-xs text-slate-500">
              {ergebnisCount} {ergebnisCount === 1 ? 'Item' : 'Items'} erfasst
            </div>
          </div>
          <span
            className={`shrink-0 rounded-md border px-2 py-1 text-xs font-medium ${
              kontrollgang.abgeschlossen
                ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                : 'bg-amber-100 text-amber-800 border-amber-200'
            }`}
          >
            {kontrollgang.abgeschlossen ? 'Abgeschlossen' : 'Offen'}
          </span>
        </div>
      </Link>
    </li>
  );
}
